import fs from "node:fs";
import path from "node:path";
import readline from "node:readline";
import { findWorkspaceRoot } from "../lib/config.js";
import { detectAgents, agentByName } from "../lib/skills/detect.js";
import { installAiFiles } from "./ai-files.js";

function ask(question: string): Promise<string> {
  const rl = readline.createInterface({
    input: process.stdin,
    output: process.stdout,
  });
  return new Promise((resolve) => {
    rl.question(question, (answer) => {
      rl.close();
      resolve(answer.trim());
    });
  });
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 30);
}

const TEMPLATES = {
  blank: "Empty app with a single API route and page",
  crud: "CRUD app with a database table, list, and form",
};

type ProjectType = "app" | "website" | "agent";

interface InitOptions {
  name?: string;
  template?: string;
  storage?: boolean;
  type?: string;
  /** Non-interactive: take all defaults (app / blank / no storage / dir-based name).
   *  Matches the `--yes` convention used by `npm create next-app` etc. */
  yes?: boolean;
  /** commander sets this false for `--no-ai-files`; default (undefined) installs. */
  aiFiles?: boolean;
}

async function askProjectType(opts?: InitOptions): Promise<ProjectType> {
  if (opts?.type === "app" || opts?.type === "website" || opts?.type === "agent") {
    return opts.type;
  }
  if (opts?.type) {
    console.error(`Unknown --type '${opts.type}' (expected 'app', 'website', or 'agent').`);
    process.exit(1);
  }
  if (opts?.yes) return "app";
  console.log();
  console.log("  What are you creating?");
  console.log("  1) app     — full-stack project (React + Hono + D1)");
  console.log("  2) website — public site (Astro)");
  console.log("  3) agent   — an OpenClaw agent (instructions, skills, flows)");
  console.log();
  const choice = (await ask("Type [1]: ")) || "1";
  if (choice === "2") return "website";
  if (choice === "3") return "agent";
  return "app";
}

/**
 * Detect which package manager invoked us. npm/pnpm/yarn/bun all set
 * `npm_config_user_agent` to something like `pnpm/10.15.1 npm/? ...`
 * before exec'ing the create-* package. We mirror that PM in our
 * post-scaffold "Get started" hints so users don't see `pnpm dev`
 * after running `yarn create`.
 */
function detectPackageManager(): "pnpm" | "npm" | "yarn" | "bun" {
  const ua = process.env.npm_config_user_agent ?? "";
  if (ua.startsWith("pnpm")) return "pnpm";
  if (ua.startsWith("yarn")) return "yarn";
  if (ua.startsWith("bun")) return "bun";
  return "npm";
}

export async function initCommand(dir?: string, opts?: InitOptions): Promise<void> {
  const type = await askProjectType(opts);
  if (type === "website") {
    return initWebsite(dir, opts);
  }
  if (type === "agent") {
    return initAgent(dir, opts);
  }
  return initApp(dir, opts);
}

async function initApp(dir?: string, opts?: InitOptions): Promise<void> {
  // --yes auto-fills everything: name from dir arg or "my-app", template
  // blank, no storage. Matches `npm create next-app -y` behavior.
  const yes = !!opts?.yes;
  const dirArgName = dir ? path.basename(path.resolve(dir)) : undefined;
  const name = opts?.name || (yes ? (dirArgName ?? "my-app") : (await ask("App name: ")) || "my-app");
  const slug = slugify(name);
  // Inside a workspace (and with no explicit dir), drop the app under apps/<slug>
  // so the monorepo stays tidy. An explicit dir arg always wins.
  let targetDir: string;
  if (dir) {
    targetDir = path.resolve(dir);
  } else {
    const ws = findWorkspaceRoot(process.cwd());
    targetDir = ws ? path.join(ws.dir, "apps", slug) : path.resolve(slug);
  }

  let template: string;
  if (opts?.template) {
    template = opts.template === "crud" ? "crud" : "blank";
  } else if (yes) {
    template = "blank";
  } else {
    console.log();
    console.log("  Templates:");
    console.log("  1) blank — Empty app with a single API route and page");
    console.log("  2) crud  — CRUD app with database, list, and form");
    console.log();
    const templateChoice = (await ask("Template [1]: ")) || "1";
    template = templateChoice === "2" ? "crud" : "blank";
  }

  if (fs.existsSync(targetDir) && fs.readdirSync(targetDir).length > 0) {
    console.error(`Directory ${targetDir} is not empty.`);
    process.exit(1);
  }

  fs.mkdirSync(path.join(targetDir, "src/server"), { recursive: true });
  fs.mkdirSync(path.join(targetDir, "src/client"), { recursive: true });

  let storage: boolean;
  if (opts?.storage !== undefined) {
    storage = opts.storage;
  } else if (yes) {
    storage = false;
  } else {
    const storageChoice = (await ask("Enable file uploads? [y/N]: ")) || "n";
    storage = storageChoice.toLowerCase() === "y";
  }

  // clawnify.json
  write(targetDir, "clawnify.json", JSON.stringify({
    $schema: "https://app.clawnify.com/schema/v1/clawnify.json",
    version: 1,
    name,
    description: "",
    app: { framework: "react+hono", database: true, storage },
  }, null, 2));

  // package.json
  const devDeps: Record<string, string> = {
    "@cloudflare/workers-types": "^4.0.0",
    "@tailwindcss/vite": "^4.0.0",
    "@types/react": "^19.0.0",
    "@types/react-dom": "^19.0.0",
    "@vitejs/plugin-react": "^4.0.0",
    clawnify: "^0.3.0",
    "drizzle-kit": "^0.31.0",
    tailwindcss: "^4.0.0",
    vite: "^6.0.0",
    wrangler: "^4.0.0",
  };
  write(targetDir, "package.json", JSON.stringify({
    name: slug,
    private: true,
    type: "module",
    scripts: {
      dev: "clawnify dev",
      build: "vite build",
      deploy: "clawnify deploy",
    },
    dependencies: {
      "@clawnify/db": "^0.4.1",
      "@hono/zod-openapi": "^0.19.0",
      hono: "^4.0.0",
      react: "^19.0.0",
      "react-dom": "^19.0.0",
      zod: "^3.0.0",
    },
    devDependencies: devDeps,
    engines: { node: ">=22" },
    pnpm: { onlyBuiltDependencies: ["esbuild"] },
  }, null, 2));

  // tsconfig.json
  write(targetDir, "tsconfig.json", JSON.stringify({
    compilerOptions: {
      target: "ESNext",
      module: "ESNext",
      moduleResolution: "Bundler",
      strict: true,
      lib: ["ESNext", "DOM", "DOM.Iterable"],
      jsx: "react-jsx",
      noEmit: true,
      skipLibCheck: true,
      resolveJsonModule: true,
      isolatedModules: true,
    },
    include: ["src/**/*.ts", "src/**/*.tsx"],
    exclude: ["node_modules", "dist"],
  }, null, 2));

  // vite.config.ts
  write(targetDir, "vite.config.ts", `import { defineConfig } from "vite";
import react from "@vitejs/plugin-react";
import tailwindcss from "@tailwindcss/vite";

export default defineConfig({
  plugins: [react(), tailwindcss()],
  build: {
    outDir: "dist",
    emptyOutDir: true,
  },
  server: {
    proxy: {
      "/api": {
        target: "http://localhost:8787",
        changeOrigin: true,
      },
    },
  },
});
`);

  // index.html
  write(targetDir, "index.html", `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${name}</title>
</head>
<body>
  <div id="app"></div>
  <script type="module" src="/src/client/main.tsx"></script>
</body>
</html>
`);

  // .gitignore
  write(targetDir, ".gitignore", `node_modules
dist
.wrangler
.clawnify
.env*
!.env.example
.dev.vars
data.db
data.db-wal
data.db-shm
`);

  // src/server/worker-env.d.ts — Cloudflare Workers types
  write(targetDir, "src/server/worker-env.d.ts", `/// <reference types="@cloudflare/workers-types" />
`);

  // src/server/schema.ts — Drizzle DSL, the single source of truth for tables.
  // Edit this file to evolve the schema; run `pnpm db:generate` to emit a
  // new migration file under drizzle/, then commit and deploy.
  write(targetDir, "src/server/schema.ts", `import { sqliteTable, text, integer } from "@clawnify/db";

/**
 * Example table. Replace with your own.
 *
 * Multi-tenancy reminder: every user-facing table should carry an
 * \`org_id\` column and every query should filter by the X-Clawnify-Org-Id
 * header. See docs/internal/build-a-clawnify-app.md.
 */
export const items = sqliteTable("items", {
  id: integer("id").primaryKey({ autoIncrement: true }),
  name: text("name").notNull(),
  createdAt: text("created_at").notNull().$default(() => new Date().toISOString()),
});
`);

  // drizzle.config.ts — points drizzle-kit at schema.ts. Migrations
  // are emitted to .clawnify/drizzle/ (gitignored). The CLI runs
  // drizzle-kit generate transparently on `clawnify dev` and
  // `clawnify deploy` — the user never has to think about it. Edit
  // schema.ts; that's it.
  write(targetDir, "drizzle.config.ts", `import { defineConfig } from "drizzle-kit";

export default defineConfig({
  schema: "./src/server/schema.ts",
  out: "./.clawnify/drizzle",
  dialect: "sqlite",
});
`);

  // src/server/index.ts
  if (storage) {
    write(targetDir, "src/server/index.ts", `import { OpenAPIHono } from "@hono/zod-openapi";
import { initUploads } from "./uploads";
import api from "./routes";

type Env = { Bindings: { DB: D1Database; UPLOADS: R2Bucket } };

const app = new OpenAPIHono<Env>();

app.use("*", async (c, next) => {
  initUploads(c.env.UPLOADS);
  await next();
});

app.route("/", api);

app.doc("/api/openapi.json", {
  openapi: "3.0.0",
  info: { title: "${name}", version: "1.0.0" },
});

export default app;
`);

    // src/server/uploads.ts
    write(targetDir, "src/server/uploads.ts", `let _bucket: R2Bucket;

export function initUploads(bucket: R2Bucket) {
  _bucket = bucket;
}

export async function putUpload(
  filename: string,
  data: ArrayBuffer | Uint8Array,
  contentType: string,
): Promise<string> {
  await _bucket.put(filename, data, { httpMetadata: { contentType } });
  return \`/api/uploads/\${filename}\`;
}

export async function getUpload(
  filename: string,
): Promise<{ data: ArrayBuffer; contentType: string } | null> {
  const obj = await _bucket.get(filename);
  if (!obj) return null;
  return {
    data: await obj.arrayBuffer(),
    contentType: obj.httpMetadata?.contentType || "application/octet-stream",
  };
}

export async function deleteUpload(filename: string): Promise<void> {
  await _bucket.delete(filename);
}

export async function readUploadAsBase64DataUrl(
  filename: string,
): Promise<string | null> {
  const result = await getUpload(filename);
  if (!result) return null;
  const bytes = new Uint8Array(result.data);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return \`data:\${result.contentType};base64,\${btoa(binary)}\`;
}
`);
  } else {
    write(targetDir, "src/server/index.ts", `import { OpenAPIHono } from "@hono/zod-openapi";
import api from "./routes";

type Env = { Bindings: { DB: D1Database } };

const app = new OpenAPIHono<Env>();

app.route("/", api);

app.doc("/api/openapi.json", {
  openapi: "3.0.0",
  info: { title: "${name}", version: "1.0.0" },
});

export default app;
`);
  }

  // src/client/index.css
  write(targetDir, "src/client/index.css", `@import "tailwindcss";
`);

  // src/client/main.tsx
  write(targetDir, "src/client/main.tsx", `import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./app";
import "./index.css";

createRoot(document.getElementById("app")!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
`);

  if (template === "crud") {
    writeCrudTemplate(targetDir, name);
  } else {
    writeBlankTemplate(targetDir, name);
  }

  // Make the scaffolded app agent-ready: rules + skills + MCP config.
  // Fresh dir has no agent marker, so default to Claude. Opt out with
  // `clawnify init --no-ai-files`.
  if (opts?.aiFiles !== false) {
    const detected = detectAgents(targetDir);
    const agents = detected.length > 0 ? detected : [agentByName("claude")];
    console.log();
    console.log("  Installing AI files (rules + skills + MCP):");
    installAiFiles(targetDir, agents, { mcp: true });
  }

  const pm = detectPackageManager();
  console.log();
  console.log(`  Created ${name} in ${path.relative(process.cwd(), targetDir)}/`);
  console.log();
  console.log("  Get started:");
  console.log(`    cd ${path.relative(process.cwd(), targetDir)}`);
  console.log(`    ${pm} install`);
  console.log(`    ${pm} ${pm === "npm" ? "run " : ""}dev          # local dev at http://localhost:5173`);
  console.log(`    ${pm} ${pm === "npm" ? "run " : ""}deploy       # deploy to production`);
  console.log();
}

async function initWebsite(dir?: string, opts?: InitOptions): Promise<void> {
  const name = opts?.name || (await ask("Site name: ")) || "my-site";
  const slug = slugify(name);
  // Inside a workspace (and with no explicit dir), the org's single website
  // lives at website/. An explicit dir arg always wins.
  let targetDir: string;
  if (dir) {
    targetDir = path.resolve(dir);
  } else {
    const ws = findWorkspaceRoot(process.cwd());
    targetDir = ws ? path.join(ws.dir, "website") : path.resolve(slug);
  }

  if (fs.existsSync(targetDir)) {
    // The workspace scaffold pre-creates website/.gitkeep — tolerate that lone
    // placeholder (and clear it), but refuse any other existing content.
    const entries = fs.readdirSync(targetDir);
    const onlyGitkeep = entries.length === 1 && entries[0] === ".gitkeep";
    if (entries.length > 0 && !onlyGitkeep) {
      console.error(`Directory ${targetDir} is not empty.`);
      process.exit(1);
    }
    if (onlyGitkeep) fs.rmSync(path.join(targetDir, ".gitkeep"));
  }

  fs.mkdirSync(targetDir, { recursive: true });

  write(targetDir, "clawnify.json", JSON.stringify({
    $schema: "https://app.clawnify.com/schema/v1/clawnify.json",
    version: 1,
    name,
    description: "",
    website: { framework: "astro" },
  }, null, 2));

  write(targetDir, "package.json", JSON.stringify({
    name: slug,
    private: true,
    type: "module",
    scripts: {
      dev: "astro dev",
      build: "astro build",
      preview: "astro preview",
      deploy: "clawnify deploy",
    },
    dependencies: {
      "@astrojs/cloudflare": "^12.0.0",
      "@tailwindcss/vite": "^4.0.0",
      astro: "^5.0.0",
      tailwindcss: "^4.0.0",
    },
    devDependencies: {
      clawnify: "^0.1.15",
      "wrangler": "^4.0.0",
    },
    engines: { node: ">=22" },
  }, null, 2));

  write(targetDir, "tsconfig.json", JSON.stringify({
    extends: "astro/tsconfigs/strict",
    include: ["src/**/*", ".astro/types.d.ts"],
    exclude: ["dist", "node_modules"],
  }, null, 2));

  write(targetDir, "astro.config.mjs", `import { defineConfig } from "astro/config";
import cloudflare from "@astrojs/cloudflare";
import tailwindcss from "@tailwindcss/vite";

export default defineConfig({
  output: "server",
  adapter: cloudflare(),
  vite: {
    plugins: [tailwindcss()],
  },
});
`);

  write(targetDir, ".gitignore", `node_modules
dist
.astro
.wrangler
.clawnify
.env*
!.env.example
.dev.vars
`);

  write(targetDir, "src/styles/global.css", `@import "tailwindcss";

:root {
  --color-primary: oklch(0.55 0.18 250);
  --color-ink: oklch(0.2 0.02 250);
  --color-canvas: oklch(0.99 0 0);
}

body {
  background-color: var(--color-canvas);
  color: var(--color-ink);
  font-family: ui-sans-serif, system-ui, sans-serif;
}
`);

  write(targetDir, "src/layouts/BaseLayout.astro", `---
interface Props {
  title?: string;
  description?: string;
}

const { title = "${name}", description = "" } = Astro.props;
---
<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>{title}</title>
    {description && <meta name="description" content={description} />}
    <link rel="stylesheet" href="/src/styles/global.css" />
  </head>
  <body>
    <slot />
  </body>
</html>
`);

  write(targetDir, "src/pages/index.astro", `---
import BaseLayout from "../layouts/BaseLayout.astro";
---
<BaseLayout title="${name}">
  <main class="mx-auto max-w-3xl px-6 py-24">
    <h1 class="text-5xl font-semibold tracking-tight">${name}</h1>
    <p class="mt-4 text-lg opacity-70">
      Edit <code>src/pages/index.astro</code> to get started.
    </p>
    <p class="mt-8">
      <a class="underline" href="https://docs.astro.build" target="_blank" rel="noreferrer">
        Astro docs →
      </a>
    </p>
  </main>
</BaseLayout>
`);

  console.log();
  console.log(`  Created ${name} in ${path.relative(process.cwd(), targetDir)}/`);
  console.log();
  console.log("  Get started:");
  console.log(`    cd ${path.relative(process.cwd(), targetDir)}`);
  console.log("    pnpm install");
  console.log("    pnpm dev          # local dev at http://localhost:4321");
  console.log("    pnpm deploy       # deploy as your org's website");
  console.log();
}

async function initAgent(dir?: string, opts?: InitOptions): Promise<void> {
  const yes = !!opts?.yes;
  const dirArgName = dir ? path.basename(path.resolve(dir)) : undefined;
  const name =
    opts?.name ||
    (yes ? (dirArgName ?? "my-agent") : (await ask("Agent name: ")) || "my-agent");
  const slug = slugify(name);
  // Inside a workspace (and with no explicit dir), agents live under agents/.
  // An explicit dir arg always wins.
  let targetDir: string;
  if (dir) {
    targetDir = path.resolve(dir);
  } else {
    const ws = findWorkspaceRoot(process.cwd());
    targetDir = ws ? path.join(ws.dir, "agents", slug) : path.resolve(slug);
  }

  if (fs.existsSync(targetDir) && fs.readdirSync(targetDir).length > 0) {
    console.error(`Directory ${targetDir} is not empty.`);
    process.exit(1);
  }

  // clawnify.json — the `agent` block discriminates the kind (mirrors
  // app/website/workspace); `harness` selects the runtime adapter.
  write(targetDir, "clawnify.json", JSON.stringify({
    $schema: "https://app.clawnify.com/schema/v1/clawnify.json",
    version: 1,
    name,
    description: "",
    agent: { harness: "openclaw" },
  }, null, 2));

  // AGENT.md — the agent's instructions (its "system prompt" as prose).
  write(targetDir, "AGENT.md", `# ${name}

You are ${name}, an OpenClaw agent.

Describe the agent's role, tone, and boundaries here. This file is the
agent's primary instructions — edit it to shape behavior.

## Skills

Reusable procedures live under \`skills/<name>/\`: a \`SKILL.md\` the agent
reads when triggered, plus a \`skill.json\` describing when it runs.

## Flows

Automations live under \`flows/<name>.json\` — ClawFlow definitions that run
on triggers (webhooks, schedules) without a full agent turn.
`);

  // Payload dirs the OpenClaw adapter deploys (see ADR § Layer 1). Kept in
  // git even when empty so the layout is discoverable.
  write(targetDir, "skills/.gitkeep", "");
  write(targetDir, "flows/.gitkeep", "");

  // .gitignore — the ADR's hard secret-exclusion guard: credentials, env, and
  // runtime state (memories, sessions, logs) are never shipped with the agent.
  // Each installer brings their own keys.
  write(targetDir, ".gitignore", `node_modules
.clawnify
.env*
!.env.example
.dev.vars
# Runtime state — never shipped with the agent (ADR § secret-exclusion)
memories/
sessions/
logs/
`);

  const rel = path.relative(process.cwd(), targetDir) || ".";
  console.log();
  console.log(`  Created agent ${name} in ${rel}/`);
  console.log();
  console.log("  Layout:");
  console.log("    AGENT.md        — the agent's instructions");
  console.log("    skills/         — reusable procedures (SKILL.md + skill.json)");
  console.log("    flows/          — ClawFlow automations (<name>.json)");
  console.log();
  console.log("  Author here and commit. Deploying this (config push to the");
  console.log("  running agent via CAP) lands in a follow-up.");
  console.log();
}

function writeBlankTemplate(dir: string, name: string) {
  // No schema.sql — the build pipeline reads drizzle/*.sql (emitted
  // by drizzle-kit generate) instead. schema.ts is the single source.

  write(dir, "src/server/routes.ts", `import { OpenAPIHono, createRoute, z } from "@hono/zod-openapi";
import { getDB, desc } from "@clawnify/db";
import * as schema from "./schema";

type Env = { Bindings: { DB: D1Database } };
const api = new OpenAPIHono<Env>();

const ItemSchema = z.object({
  id: z.number(),
  name: z.string(),
  createdAt: z.string(),
}).openapi("Item");

api.openapi(
  createRoute({
    method: "get",
    path: "/api/items",
    summary: "List items",
    responses: {
      200: {
        description: "List of items",
        content: { "application/json": { schema: z.array(ItemSchema) } },
      },
    },
  }),
  async (c) => {
    const db = getDB(c.env, { schema });
    const items = await db
      .select()
      .from(schema.items)
      .orderBy(desc(schema.items.createdAt));
    return c.json(items);
  },
);

export default api;
`);

  write(dir, "src/client/app.tsx", `export function App() {
  return (
    <main className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 px-6">
      <div className="max-w-xl w-full text-center">
        <div className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur px-3 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200">
          <span className="size-1.5 rounded-full bg-emerald-500" />
          Clawnify · live
        </div>

        <h1 className="mt-6 text-4xl font-semibold tracking-tight text-slate-900">
          Welcome to ${name}
        </h1>
        <p className="mt-3 text-base text-slate-600">
          Edit <code className="rounded bg-slate-200/70 px-1.5 py-0.5 text-sm">src/client/app.tsx</code> to get started.
        </p>

        <ul className="mt-8 grid gap-3 text-left text-sm text-slate-700">
          <li className="rounded-lg border border-slate-200 bg-white px-4 py-3">
            <span className="font-medium text-slate-900">Try the API.</span> Open <code>/api/items</code> — empty list to start.
          </li>
          <li className="rounded-lg border border-slate-200 bg-white px-4 py-3">
            <span className="font-medium text-slate-900">Evolve the schema.</span> Edit <code>src/server/schema.ts</code>, then run <code>pnpm db:generate</code>.
          </li>
          <li className="rounded-lg border border-slate-200 bg-white px-4 py-3">
            <span className="font-medium text-slate-900">Deploy.</span> Run <code>pnpm deploy</code> to push live.
          </li>
        </ul>

        <p className="mt-8 text-xs text-slate-500">
          Read{" "}
          <a href="https://github.com/clawnify/clawnify/blob/main/docs/internal/build-a-clawnify-app.md" target="_blank" rel="noreferrer" className="underline underline-offset-2">
            docs/internal/build-a-clawnify-app.md
          </a>{" "}
          for the full gold-standard guide.
        </p>
      </div>
    </main>
  );
}
`);
}

function writeCrudTemplate(dir: string, name: string) {
  // CRUD template extends the default schema with title + status.
  // Overwrites the schema.ts that initApp wrote (a minimal items
  // table). drizzle-kit generates the matching migration on first
  // `clawnify dev` / `clawnify deploy` — no SQL file shipped here.
  write(dir, "src/server/schema.ts", `import { sqliteTable, text, integer } from "@clawnify/db";

export const items = sqliteTable("items", {
  id: integer("id").primaryKey({ autoIncrement: true }),
  title: text("title").notNull(),
  status: text("status").notNull().default("active"),
  createdAt: text("created_at").notNull().$default(() => new Date().toISOString()),
});
`);

  write(dir, "src/server/routes.ts", `import { OpenAPIHono, createRoute, z } from "@hono/zod-openapi";
import { getDB, eq, desc } from "@clawnify/db";
import * as schema from "./schema";

type Env = { Bindings: { DB: D1Database } };
const api = new OpenAPIHono<Env>();

const ItemSchema = z.object({
  id: z.number(),
  title: z.string(),
  status: z.string(),
  createdAt: z.string(),
}).openapi("Item");

const CreateItemSchema = z.object({ title: z.string().min(1) }).openapi("CreateItem");
const UpdateItemSchema = z.object({
  title: z.string().min(1).optional(),
  status: z.string().optional(),
}).openapi("UpdateItem");
const IdParamSchema = z.object({ id: z.string() });
const OkSchema = z.object({ ok: z.boolean() }).openapi("Ok");

api.openapi(
  createRoute({
    method: "get",
    path: "/api/items",
    summary: "List items",
    responses: {
      200: {
        description: "List of items",
        content: { "application/json": { schema: z.array(ItemSchema) } },
      },
    },
  }),
  async (c) => {
    const db = getDB(c.env, { schema });
    const items = await db
      .select()
      .from(schema.items)
      .orderBy(desc(schema.items.createdAt));
    return c.json(items);
  },
);

api.openapi(
  createRoute({
    method: "post",
    path: "/api/items",
    summary: "Create item",
    request: { body: { content: { "application/json": { schema: CreateItemSchema } } } },
    responses: {
      201: {
        description: "Created item",
        content: { "application/json": { schema: ItemSchema } },
      },
    },
  }),
  async (c) => {
    const { title } = c.req.valid("json");
    const db = getDB(c.env, { schema });
    const [row] = await db
      .insert(schema.items)
      .values({ title: title.trim() })
      .returning();
    return c.json(row, 201);
  },
);

api.openapi(
  createRoute({
    method: "patch",
    path: "/api/items/:id",
    summary: "Update item",
    request: {
      params: IdParamSchema,
      body: { content: { "application/json": { schema: UpdateItemSchema } } },
    },
    responses: {
      200: { description: "Updated", content: { "application/json": { schema: OkSchema } } },
    },
  }),
  async (c) => {
    const { id } = c.req.valid("param");
    const body = c.req.valid("json");
    const patch: Partial<typeof schema.items.$inferInsert> = {};
    if (body.title !== undefined) patch.title = body.title;
    if (body.status !== undefined) patch.status = body.status;
    if (Object.keys(patch).length === 0) return c.json({ ok: false }, 400);
    const db = getDB(c.env, { schema });
    await db.update(schema.items).set(patch).where(eq(schema.items.id, Number(id)));
    return c.json({ ok: true });
  },
);

api.openapi(
  createRoute({
    method: "delete",
    path: "/api/items/:id",
    summary: "Delete item",
    request: { params: IdParamSchema },
    responses: {
      200: { description: "Deleted", content: { "application/json": { schema: OkSchema } } },
    },
  }),
  async (c) => {
    const { id } = c.req.valid("param");
    const db = getDB(c.env, { schema });
    await db.delete(schema.items).where(eq(schema.items.id, Number(id)));
    return c.json({ ok: true });
  },
);

export default api;
`);

  write(dir, "src/client/app.tsx", `import { useState, useEffect } from "react";

interface Item {
  id: number;
  title: string;
  status: string;
  createdAt: string;
}

export function App() {
  const [items, setItems] = useState<Item[]>([]);
  const [title, setTitle] = useState("");

  async function load() {
    const res = await fetch("/api/items");
    setItems(await res.json());
  }

  useEffect(() => { load(); }, []);

  async function addItem(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    await fetch("/api/items", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title }),
    });
    setTitle("");
    load();
  }

  async function toggleStatus(item: Item) {
    const next = item.status === "active" ? "done" : "active";
    await fetch(\`/api/items/\${item.id}\`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: next }),
    });
    load();
  }

  async function deleteItem(id: number) {
    await fetch(\`/api/items/\${id}\`, { method: "DELETE" });
    load();
  }

  return (
    <div className="max-w-xl mx-auto mt-10 px-4">
      <h1 className="text-2xl font-semibold mb-6">${name}</h1>
      <form onSubmit={addItem} className="flex gap-2 mb-6">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Add an item..."
          className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-black"
        />
        <button
          type="submit"
          className="px-4 py-2 rounded-lg bg-black text-white text-sm font-medium hover:bg-gray-800"
        >
          Add
        </button>
      </form>
      <ul className="divide-y divide-gray-100">
        {items.map((item) => (
          <li key={item.id} className="flex items-center gap-3 py-2">
            <input
              type="checkbox"
              checked={item.status === "done"}
              onChange={() => toggleStatus(item)}
              className="rounded"
            />
            <span
              className={\`flex-1 text-sm \${item.status === "done" ? "line-through text-gray-400" : "text-gray-800"}\`}
            >
              {item.title}
            </span>
            <button
              onClick={() => deleteItem(item.id)}
              className="text-gray-300 hover:text-gray-600 text-lg leading-none"
            >
              ×
            </button>
          </li>
        ))}
      </ul>
      {items.length === 0 && (
        <p className="text-gray-400 text-sm mt-4">No items yet. Add one above.</p>
      )}
    </div>
  );
}
`);
}

function write(dir: string, file: string, content: string) {
  const fullPath = path.join(dir, file);
  fs.mkdirSync(path.dirname(fullPath), { recursive: true });
  fs.writeFileSync(fullPath, content);
}
